/**
 * What the board shows for a card face (`CardRef`): name, type, stats, rules text and art sources, from
 * the pinned catalogue; tokens use `@play/catalog`'s display table (styled placeholders, owner decision
 * Q46). Pure functions of `(catalog, ref)`.
 */
import {
  type ArtWidth,
  type Catalog,
  type CatalogCard,
  type CatalogPrinting,
  artSources,
  displayCardName,
  isLandscapeImage,
  tokenDisplay,
} from '@play/catalog';
import type { CardRef } from '@play/protocol';
import { artConfig } from './env';

/** Display facts about one card face. */
export interface CardFace {
  cardId: string;
  name: string;
  /** `Unit`, `Spell`, `Gear`, `Legend`, `Battlefield`, `Rune`, or `Token`. */
  type: string;
  supertype: string | null;
  domains: string[];
  energy: number | null;
  power: number | null;
  might: number | null;
  tags: string[];
  keywords: string[];
  /** Curated rules text (`RulesText` oracle lines). */
  oracle: string;
  isToken: boolean;
  landscape: boolean;
  printing: CatalogPrinting | null;
  /** Accessible description: Riot's alt text when present. */
  alt: string;
}

const TOKEN_PREFIX = 'token:';

function fromCard(catalog: Catalog, card: CatalogCard, printingId: string | null): CardFace {
  const printing = (printingId ? catalog.printing(printingId) : undefined) ?? catalog.plainPrinting(card.id) ?? null;
  return {
    cardId: card.id,
    name: displayCardName(card.name),
    type: card.type,
    supertype: card.supertype,
    domains: card.domains,
    energy: card.energy,
    power: card.power,
    might: card.might,
    tags: card.tags,
    keywords: card.keywords,
    oracle: card.oracle,
    isToken: false,
    landscape: card.type === 'Battlefield' || (printing ? isLandscapeImage(printing.imageUrl) : false),
    printing,
    alt: printing?.accessibilityText ?? `${card.name} (${card.type})`,
  };
}

/** The face of a card reference; unknown ids get a neutral placeholder face. */
export function cardFace(catalog: Catalog | null, ref: CardRef): CardFace {
  if (ref.cardId.startsWith(TOKEN_PREFIX)) {
    const token = tokenDisplay(ref.cardId);
    const art = token?.catalogCardId && catalog ? catalog.card(token.catalogCardId) : undefined;
    const printingId = ref.printingId ?? token?.printingId ?? null;
    const printing = printingId && catalog ? (catalog.printing(printingId) ?? null) : null;
    const name = token?.name ?? ref.cardId.slice(TOKEN_PREFIX.length);
    const battlefield = ref.cardId === 'token:brush' || ref.cardId === 'token:baron-pit';
    return {
      cardId: ref.cardId,
      name,
      type: battlefield ? 'Battlefield' : 'Token',
      supertype: 'Token',
      domains: art?.domains ?? [],
      energy: null,
      power: null,
      might: art?.might ?? tokenMight(token?.text),
      tags: art?.tags ?? [],
      keywords: [],
      oracle: token?.text ?? '',
      isToken: true,
      landscape: battlefield,
      printing,
      alt: `${name} token. ${token?.text ?? ''}`.trim(),
    };
  }
  const card = catalog?.card(ref.cardId);
  if (card) return fromCard(catalog as Catalog, card, ref.printingId);
  return {
    cardId: ref.cardId,
    name: catalog ? ref.cardId : 'Loading…',
    type: 'Unit',
    supertype: null,
    domains: [],
    energy: null,
    power: null,
    might: null,
    tags: [],
    keywords: [],
    oracle: '',
    isToken: false,
    landscape: false,
    printing: null,
    alt: ref.cardId,
  };
}

function tokenMight(text: string | undefined): number | null {
  const m = text ? /^(\d+) Might/.exec(text) : null;
  return m?.[1] ? Number(m[1]) : null;
}

/** Image URLs to try for a face at a width (mirror, then Riot CDN); empty = styled placeholder. */
export function faceSources(face: CardFace, width: ArtWidth): string[] {
  return face.printing ? artSources(face.printing, width, artConfig) : [];
}

/** The catalogue name for a card id (log lines, Porodigy summaries). */
export function cardName(catalog: Catalog | null, cardId: string): string {
  return cardFace(catalog, { cardId, printingId: null }).name;
}
